import { Link } from "@tanstack/react-router";
import type { Element } from "hast";
import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { MermaidBlock } from "@/components/mermaid/mermaid-block";
import { FigmaContextCards } from "@/features/figma/components/figma-context-cards";
import { classifyLink } from "@/lib/app-link";
import { parseAskUserBlock, parseKairoDocListBlock, splitMessageContent } from "../chat.utils";
import { ChatKairoDocList } from "./chat-kairo-doc-list";
import { ChatMultiselectQuestion } from "./chat-multiselect-question";

/** True when a hast `code` element comes from a ```mermaid fence (CR-21). */
function isMermaidCode(node: Element | undefined): boolean {
  const className = node?.properties?.className;
  return Array.isArray(className) && className.includes("language-mermaid");
}

function childrenToText(children: ReactNode): string {
  if (Array.isArray(children)) return children.map((child) => childrenToText(child)).join("");
  return typeof children === "string" || typeof children === "number" ? String(children) : "";
}

interface AiMessageContentProps {
  content: string;
  /** CR-126: a reply still streaming has half-written links — no Figma cards yet. */
  isStreaming?: boolean;
  askUserAnswered: boolean;
  askUserAnswerContent?: string;
  onSubmitAskUserAnswer?: (answer: string) => Promise<void>;
}

/**
 * Renders an AI-authored message body: markdown text plus the interactive
 * ```ask-user and ```kairo-doc-list widget blocks. Shared between chat
 * bubbles and document comment threads (CR-23).
 */
export function AiMessageContent({
  content,
  isStreaming = false,
  askUserAnswered,
  askUserAnswerContent,
  onSubmitAskUserAnswer,
}: AiMessageContentProps) {
  const parts = splitMessageContent(content);
  // CR-126 (D7): Figma context cards for the links in the text, after the message.
  const linkText = parts
    .filter((part) => part.type === "text")
    .map((part) => part.content)
    .join("\n");
  return (
    <>
      {parts.map((part, i) => {
        if (part.type === "kairo-doc-list") {
          const docList = parseKairoDocListBlock(part.raw);
          if (!docList) return null;
          return <ChatKairoDocList key={i} data={docList} />;
        }
        if (part.type === "ask-user") {
          const questionData = parseAskUserBlock(part.raw);
          if (!questionData) return null;
          return (
            <ChatMultiselectQuestion
              key={i}
              question={questionData}
              isAnswered={askUserAnswered}
              answerContent={askUserAnswerContent}
              onSubmit={onSubmitAskUserAnswer ?? (() => Promise.resolve())}
            />
          );
        }
        return (
          <div key={i} className="markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkBreaks]}
              components={{
                // Mermaid fences render as diagrams; the pre override unwraps
                // them so the diagram div never nests inside a <pre>.
                pre: ({ node, children, ...props }) => {
                  const codeChild = node?.children.find(
                    (child): child is Element =>
                      child.type === "element" && child.tagName === "code",
                  );
                  if (isMermaidCode(codeChild)) return <>{children}</>;
                  return <pre {...props}>{children}</pre>;
                },
                code: ({ node, className, children, ...props }) => {
                  if (isMermaidCode(node)) {
                    return <MermaidBlock code={childrenToText(children)} />;
                  }
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
                a: ({ href, children }) => {
                  // CR-163: one classifier — `//host` and `/\host` leave the app.
                  const link = classifyLink(href);
                  if (link.kind === "internal") {
                    return (
                      <Link to={link.path} preload="intent">
                        {children}
                      </Link>
                    );
                  }
                  if (link.kind === "external") {
                    return (
                      <a href={link.href} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    );
                  }
                  return <span>{children}</span>;
                },
              }}
            >
              {part.content}
            </ReactMarkdown>
          </div>
        );
      })}
      {isStreaming ? null : <FigmaContextCards text={linkText} />}
    </>
  );
}
